import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Accordion, AccordionSummary, AccordionDetails, TextField, CircularProgress, Alert } from '@mui/material';
import { BIGQUERY_CONSTANTS } from 'constants';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { validateSQL, DANGEROUS_OPERATORS_LIST } from 'helpers/sqlValidation';
import { validateListCutterJobName } from 'helpers/validateListCutterJobName';
import { getCsrfToken } from 'utils/csrf';
import ViewTableInBigQueryButton from 'components/bigquery/ViewTableInBigQueryButton';
import { InlineEditableField } from 'components/editable';
import { Link } from 'react-router-dom';

const ConfirmNewListCutterJob = ({ projectId, sourceDataset, sourceTable, sourceVoterfileType, selectedColumns, columnsWithFilters, orderByConditions, hasLimit, limitValue, initialTargetDataset, initialTargetTable, onNavigateToStep }) => {
  const [loading, setLoading] = useState(false);
  const [csrftoken, setCsrftoken] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobSubmitted, setJobSubmitted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [jobState, setJobState] = useState('');
  const [jobSQL, setJobSQL] = useState('');
  const [targetDataset, setTargetDataset] = useState(initialTargetDataset);
  const [targetTable, setTargetTable] = useState(initialTargetTable);
  const [jobName, setJobName] = useState('');
  const [defaultJobName, setDefaultJobName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isValidatingName, setIsValidatingName] = useState(false);
  const [sqlValidationError, setSqlValidationError] = useState(null);
  const [sqlErrorType, setSqlErrorType] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [sqlFetched, setSqlFetched] = useState(false);
  const [tableExists, setTableExists] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter out order by conditions that don't have a column specified (this is easier than doing proper input validation, which was vexing - but you could TODO that if you want)
  const validOrderByConditions = orderByConditions.filter(condition => 
    condition.column && condition.column.trim() !== ''
  );

  useEffect(() => {
    const token = getCsrfToken();
    setCsrftoken(token);
  }, []);

  useEffect(() => {
    const fetchSQL = async () => {
      // Skip if we've already fetched SQL once
      if (sqlFetched) return;
      
      try {
        const freshToken = getCsrfToken();
        
        const formattedSelectedColumns = selectedColumns.map(col => 
          typeof col === 'string' ? col : col.column
        );

        const formattedFilters = columnsWithFilters || [];
        
        const response = await fetch('/list-cutter/api/generate-sql/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': freshToken,
          },
          mode: 'same-origin',
          body: JSON.stringify({
            project_id: projectId,
            source_dataset: sourceDataset,
            source_table: sourceTable,
            target_dataset: targetDataset,
            target_table: targetTable,
            selected_columns: formattedSelectedColumns,
            filters: formattedFilters,
            order_by_conditions: validOrderByConditions,
            limit: limitValue
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setJobSQL(data.sql);
        setAnalysis(data.analysis);
        
        // Mark that we've fetched the SQL
        setSqlFetched(true);
      } catch (error) {
        console.error('Error fetching SQL:', error);
        setJobSQL('Error generating SQL query');
      }
    };

    if (projectId && sourceDataset && sourceTable && targetDataset && targetTable) {
      fetchSQL();
    }
  }, [projectId, sourceDataset, sourceTable, targetDataset, targetTable, selectedColumns, columnsWithFilters, validOrderByConditions, limitValue, sqlFetched]);

  useEffect(() => {
    if (!jobSQL || jobSQL === 'Error generating SQL query') return;

    // Extract everything after WHERE and validate it
    const whereClauseMatch = jobSQL.match(/WHERE\s+(.*)/i);
    
    if (whereClauseMatch) {
      const afterWhere = whereClauseMatch[1];
      
      const validation = validateSQL(afterWhere);
      
      if (!validation.isValid) {
        setSqlValidationError(validation.error);
        setSqlErrorType(validation.errorType);
      } else {
        setSqlValidationError(null);
        setSqlErrorType(null);
      }
    } else {
    }
  }, [jobSQL]);

  // Add effect to generate default name
  useEffect(() => {
    if (sourceDataset && sourceTable && targetDataset && targetTable) {
      const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19);
      setDefaultJobName(`Custom List ${timestamp}`);
      if (!jobName) {
        setJobName(`Custom List ${timestamp}`);
      }
    }
  }, [sourceDataset, sourceTable, targetDataset, targetTable]);

  // Add debounce function to avoid too many API calls
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Validate name against existing jobs
  const validateName = async (name) => {
    if (!name) {
      setNameError('Please provide a name for this job');
      return false;
    }

    try {
      setIsValidatingName(true);
      const validation = await validateListCutterJobName(name);
      
      if (!validation.isValid) {
        setNameError(validation.errorMessage);
        return false;
      }

      setNameError('');
      return true;
    } catch (error) {
      console.error('Error validating name:', error);
      setNameError('Error checking name availability');
      return false;
    } finally {
      setIsValidatingName(false);
    }
  };

  // Debounced version of validateName
  const debouncedValidate = debounce(validateName, 300);

  // Handle name change with validation
  const handleNameChange = (e) => {
    const newName = e.target.value;
    setJobName(newName);
    setIsValidatingName(true);
    debouncedValidate(newName);
  };

  const targetTableRef = `${targetDataset}.${targetTable}`;

  const handleSubmit = async () => {
    // Validate name one final time before submitting
    const isNameValid = await validateName(jobName);
    if (!isNameValid) {
      return;
    }

    // Set loading and submitting states to true
    setLoading(true);
    setIsSubmitting(true);
    
    try {
      // Get a fresh CSRF token
      const freshToken = getCsrfToken();
      
      // First submit the BigQuery job
      const bqResponse = await fetch('/bigquery/submit-bigquery-job/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': freshToken,
        },
        mode: 'same-origin',
        body: JSON.stringify({
          project_id: projectId,
          target_dataset_id: targetDataset,
          target_table_id: targetTable,
          sql: jobSQL,
        }),
      });

      if (!bqResponse.ok) {
        throw new Error(`HTTP error! status: ${bqResponse.status}`);
      }

      const bqData = await bqResponse.json();
      
      // After successful BigQuery job submission, create the job config
      const configResponse = await fetch('/list-cutter/api/jobs/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': freshToken,
        },
        mode: 'same-origin',
        body: JSON.stringify({
          project_id: projectId,
          name: jobName,
          source_dataset: sourceDataset,
          source_table: sourceTable,
          source_voterfile_type: sourceVoterfileType,
          target_dataset: targetDataset,
          target_table: targetTable,
          selected_columns: selectedColumns,
          filters: columnsWithFilters,
          order_by_conditions: validOrderByConditions,
          limit: limitValue,
          has_limit: Boolean(limitValue),
          generated_sql: jobSQL,
          execution_start_time: new Date().toISOString(),
          state: 'PENDING',
          bigquery_job_id: bqData.job_id
        }),
      });

      if (!configResponse.ok) {
        throw new Error('Failed to create job configuration');
      }

      const configData = await configResponse.json();
      setJobId(configData.id);
      setJobSubmitted(true);
      setMessages(prevMessages => [...prevMessages, { text: 'Job submitted successfully. Checking status...', color: 'blue' }]);
      pollJobStatus(bqData.job_id, projectId, configData.id);
    } catch (error) {
      console.error('Error submitting job:', error);
      setMessages(prevMessages => [...prevMessages, { text: 'Error submitting job: ' + error.message, color: 'red' }]);
      
      // On error, we reset the isSubmitting state to allow the user to try again
      setIsSubmitting(false);
    } finally {
      setLoading(false);
      // Note: We intentionally don't reset isSubmitting here, 
      // as we want the button to remain disabled after successful submission
    }
  };

  const pollJobStatus = (jobId, projectId, jobConfigId) => {
    const startTime = Date.now();
    
    const intervalId = setInterval(async () => {
      try {
        // Check if we've exceeded the timeout
        if (Date.now() - startTime >= BIGQUERY_CONSTANTS.POLLING_TIMEOUT_MS) {
          clearInterval(intervalId);
          setMessages(prevMessages => [
            ...prevMessages, 
            { 
              text: 'Job status polling timed out. The job may still be running in the background.', 
              color: 'orange' 
            }
          ]);
          setJobSubmitted(true);
          
          // Store the job config ID to enable navigation to job details page
          setJobId(jobConfigId);
          return;
        }

        const response = await fetch(`/bigquery/check-bigquery-job/?job_id=${jobId}&project_id=${projectId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const jobData = await response.json();
        setJobState(jobData.state);

        // Update the job state in the ListCutterJob object
        await fetch(`/list-cutter/api/jobs/${jobConfigId}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
          },
          body: JSON.stringify({
            state: jobData.state === 'DONE' && jobData.errors ? 'FAILED' : jobData.state,
            errors: jobData.errors || null
          })
        });
        
        if (jobData.state === 'DONE' && !jobData.errors) {
          clearInterval(intervalId);
          setMessages(prevMessages => [...prevMessages, { text: 'Job completed successfully. Congratulations!', color: 'green' }]);
          setJobSubmitted(true);
        } else if (jobData.state === 'FAILED' || (jobData.state === 'DONE' && jobData.errors)) {
          clearInterval(intervalId);
          setMessages(prevMessages => [
            ...prevMessages, 
            { 
              text: (
                <Box sx={{ color: 'darkred' }}>
                  There was an error while running your job :(
                  <Box 
                    component="pre" 
                    sx={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: 2, 
                      borderRadius: 1, 
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {jobData.errors.map(error => error.message).join(', ')}
                  </Box>
                </Box>
              )
            }
          ]);
          setJobSubmitted(true);
        } else {
          setMessages(prevMessages => [...prevMessages, { text: 'Waiting for job to complete...', color: 'black', fontStyle: 'italic' }]);
        }
      } catch (error) {
        console.error('Error fetching job status:', error);
        clearInterval(intervalId);
      }
    }, BIGQUERY_CONSTANTS.POLLING_INTERVAL_MS);
  };

  const getSqlErrorMessage = () => {
    if (sqlErrorType === 'disallowedKeyword') {
      return `SQL contains disallowed operators: ${DANGEROUS_OPERATORS_LIST.join(", ")}`;
    }
    return sqlValidationError;
  };

  // Add this effect to check if the table exists when component loads
  useEffect(() => {
    const checkTableExists = async () => {
      if (!projectId || !targetDataset || !targetTable) return;
      
      try {
        const queryParams = new URLSearchParams({
          project_id: projectId,
          dataset_id: targetDataset
        });
        
        const url = `/bigquery/list-tables/?${queryParams.toString()}`;
        const response = await fetch(url);
        
        if (!response.ok) {
          console.error("Failed to check if table exists:", response.status);
          return;
        }
        
        const tablesList = await response.json();
        const tableExists = tablesList.includes(targetTable);
        
        if (tableExists) {
          setTableExists(true);
        } else {
          setTableExists(false);
        }
      } catch (error) {
        console.error('Error checking if table exists:', error);
      }
    };

    checkTableExists();
  }, [projectId, targetDataset, targetTable]);

  const handleTableNameChange = async (newTableName) => {
    if (!newTableName.trim()) {
      throw new Error("Table name cannot be empty");
    }
    
    // Update the table name
    setTargetTable(newTableName);
    
    // Check if the table exists
    try {
      const queryParams = new URLSearchParams({
        project_id: projectId,
        dataset_id: targetDataset
      });
      
      const url = `/bigquery/list-tables/?${queryParams.toString()}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error("Failed to check if table exists");
      }
      
      const tablesList = await response.json();
      const tableExists = tablesList.includes(newTableName);
      
      if (tableExists) {
        setTableExists(true);
      } else {
        setTableExists(false);
      }
      
      // Reset the SQL to trigger a new fetch with the updated table name
      setSqlFetched(false);
      
      return true;
    } catch (error) {
      console.error("Error checking table existence:", error);
      throw error;
    }
  };

  return (
    <Box sx={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <Typography variant="h3" sx={{ mb: 4 }}>
        Confirm Your List Cutter Job
      </Typography>

      {/* Add Job Name Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
          Job Name
        </Typography>
        <TextField
          fullWidth
          label="Job Name"
          value={jobName}
          onChange={handleNameChange}
          size="medium"
          required
          error={Boolean(nameError)}
          helperText={nameError || (isValidatingName ? 'Checking name availability...' : '')}
          InputProps={{
            endAdornment: isValidatingName ? (
              <CircularProgress size={20} />
            ) : null
          }}
          sx={{ mb: 2 }}
        />
        {jobName !== defaultJobName && (
          <Button
            onClick={() => {
              setJobName(defaultJobName);
              validateName(defaultJobName);
            }}
            sx={{
              color: 'primary.main',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            ← Reset to default name
          </Button>
        )}
      </Box>

      {/* Source and Destination Accordion */}
      <Accordion defaultExpanded sx={{ mb: 4 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': {
              backgroundColor: '#f0f1f2',
            }
          }}
        >
          <Typography variant="h4" sx={{ fontSize: '1.25rem' }}>
            Source and Destination
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Dataset ID row */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  minHeight: '32px'
                }}>
                  <Box sx={{ width: '100px', flexShrink: 0 }}>
                    <strong>Source Dataset:</strong>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    {sourceDataset}
                  </Box>
                </Box>
                
                {/* Table ID row */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  minHeight: '32px'
                }}>
                  <Box sx={{ width: '100px', flexShrink: 0 }}>
                    <strong>Source Table:</strong>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    {sourceTable}
                  </Box>
                </Box>
                
                {/* Voterfile Type row (conditional) */}
                {(sourceVoterfileType === 'catalist' || sourceVoterfileType === 'targetsmart') && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    minHeight: '32px'
                  }}>
                    <Box sx={{ width: '100px', flexShrink: 0 }}>
                      <strong>Voterfile Type:</strong>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      {sourceVoterfileType}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {/* Dataset ID row */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  minHeight: '32px'
                }}>
                  <Box sx={{ width: '100px', flexShrink: 0 }}>
                    <strong>Destination Dataset:</strong>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    {targetDataset}
                  </Box>
                </Box>
                
                {/* Table ID row */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  minHeight: '32px'
                }}>
                  <Box sx={{ width: '100px', flexShrink: 0 }}>
                    <strong>Destination Table:</strong>
                  </Box>
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                    <InlineEditableField
                      value={targetTable}
                      onSave={handleTableNameChange}
                      displayStyles={{
                        minWidth: '120px',
                        padding: '2px 4px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        }
                      }}
                      editOnClick={true}
                      inputProps={{
                        size: "small",
                      }}
                      confirmButtonText="Update"
                      placeholder="Enter table name"
                    />
                    <Box component="span" sx={{ 
                      ml: 1, 
                      fontSize: '0.75rem', 
                      color: 'text.secondary'
                    }}>
                      (click to edit)
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 400 }}>
                Your new list will be written to:{' '}
                <Box component="span" sx={{ 
                  display: 'inline',
                  wordBreak: 'break-word', 
                  fontWeight: 700,
                  fontFamily: 'monospace'
                }}>
                  {targetTableRef}
                </Box>.
              </Typography>
              
              {/* Warning appears after "Your new list will be written to:" */}
              {tableExists && (
                <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
                  Table <Box component="span" sx={{ fontFamily: 'monospace', color: '#1976d2', fontWeight: 'bold' }}>
                    {`${targetDataset}.${targetTable}`}
                  </Box> already exists and will be overwritten. If you do not wish to overwrite the existing table, please enter a new one.
                </Alert>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Configuration Details Accordion */}
      <Accordion sx={{ mb: 4 }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': {
              backgroundColor: '#f0f1f2',
            }
          }}
        >
          <Typography variant="h4" sx={{ fontSize: '1.25rem' }}>
            Job Configuration Details (click to expand)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 3 }}>
          {/* Filters Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              Filters Applied
            </Typography>
            <Button
              onClick={() => onNavigateToStep(1)}
              sx={{ 
                mb: 2,
                color: 'primary.main',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              ← Modify
            </Button>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              The following filters will determine which rows are included in your list.
            </Typography>
            <Box sx={{ 
              backgroundColor: '#fff', 
              p: 3, 
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              {columnsWithFilters.length > 0 ? (
                columnsWithFilters.map((col, index) => (
                  <Typography key={index} variant="body1" sx={{ 
                    ml: 2, 
                    mb: 1,
                    '&:last-child': { mb: 0 }
                  }}>
                    • {col.filter}
                  </Typography>
                ))
              ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No filters applied - all rows will be included
                </Typography>
              )}
            </Box>
          </Box>

          {/* Row Count Limit and Ordering Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              Row Count Limit and Ordering
            </Typography>
            <Button
              onClick={() => onNavigateToStep(1)}
              sx={{ 
                mb: 2,
                color: 'primary.main',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              ← Modify
            </Button>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              How your results will be limited and ordered:
            </Typography>
            <Box sx={{ 
              backgroundColor: '#fff', 
              p: 3, 
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              {(orderByConditions?.length > 0 || limitValue) ? (
                <>
                  {limitValue && (
                    <Box>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Limit:
                      </Typography>
                      <Typography variant="body1" sx={{ ml: 2, mb: 1}}>
                        • Top {limitValue} rows
                      </Typography>
                    </Box>
                  )}
                  {validOrderByConditions?.length > 0 && (
                    <Box sx={{ mb: limitValue ? 2 : 0 }}>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Order by:
                      </Typography>
                      {validOrderByConditions.map((condition, index) => (
                        <Typography key={index} variant="body1" sx={{ 
                          ml: 2, 
                          mb: 1,
                          '&:last-child': { mb: 0 }
                        }}>
                          • {condition.column} ({condition.direction.toLowerCase()})
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No row limit specified - all rows meeting the criteria above will be included
                </Typography>
              )}
            </Box>
          </Box>

          {/* Columns Section */}
          <Box sx={{ mb: 0 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              Output Columns
            </Typography>
            <Button
              onClick={() => onNavigateToStep(2)}
              sx={{ 
                mb: 2,
                color: 'primary.main',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline'
                }
              }}
            >
              ← Modify
            </Button>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              These columns will be included in your output table:
            </Typography>
            <Box sx={{ 
              backgroundColor: '#fff', 
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              maxHeight: '200px',
              overflow: 'hidden'
            }}>
              <Box sx={{
                height: '100%',
                maxHeight: '200px',
                overflowY: 'auto',
                p: 3,
                pr: 2,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#bdbdbd',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#9e9e9e',
                  },
                }
              }}>
                {selectedColumns.length > 0 ? (
                  selectedColumns.map((col, index) => (
                    <Typography key={index} variant="body1" sx={{ 
                      ml: 2,
                      mb: 1,
                      '&:last-child': { mb: 0 }
                    }}>
                      • <strong>{typeof col === 'string' ? col : col.column}</strong>
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No columns selected
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* SQL Preview Section */}
      <Box sx={{ mb: 4 }}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f8f9fa',
              '&:hover': {
                backgroundColor: '#f0f1f2',
              }
            }}
          >
            <Typography variant="h4" sx={{ fontSize: '1.25rem' }}>
              SQL Preview (click to expand)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ 
              backgroundColor: '#f5f5f5', 
              p: 3, 
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              overflowX: 'auto'
            }}>
              {jobSQL}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Submit Section */}
      <Box sx={{ 
        textAlign: 'center',
        mt: 6,
        mb: 4,
        pt: 4,
        borderTop: '1px solid #e0e0e0'
      }}>
        {analysis && (
          <Typography variant="h6" sx={{ 
            mb: 2, 
            fontStyle: 'italic',
            ...(analysis.is_valid ? 
              // Styling for valid analysis (cost estimate)
              analysis.total_bytes >= ((BIGQUERY_CONSTANTS.COST_ALERT_THRESHOLD_USD / BIGQUERY_CONSTANTS.COST_PER_TB) * Math.pow(2, 40)) && {
                backgroundColor: 'warning.light',
                color: 'text.primary',
                padding: 2,
                borderRadius: 1
              }
              : 
              // Styling for invalid analysis (error message)
              {
                color: 'error.main',
                backgroundColor: 'error.light',
                padding: 2,
                borderRadius: 1
              }
            )
          }}>
            {analysis.is_valid ? 
              // Show cost estimate for valid analysis
              <>
                This job will process <strong>{analysis.total_bytes.toLocaleString()}</strong> bytes, with an estimated cost of <strong>${((analysis.total_bytes / Math.pow(2, 40)) * BIGQUERY_CONSTANTS.COST_PER_TB).toFixed(3)}</strong>.
                <br /><a href="https://cloud.google.com/bigquery/pricing#on_demand_pricing" target="_blank" rel="noopener noreferrer">Learn more about BigQuery compute costs.</a>
              </>
              :
              // Show error message for invalid analysis
              analysis.error_message
            }
          </Typography>
        )}
        <Typography variant="h6" sx={{ mb: 2, fontStyle: 'italic' }}>
          If everything looks good, click <strong>Cut It</strong> to create your list.
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={Boolean(nameError) || Boolean(sqlValidationError) || (analysis && !analysis.is_valid) || isSubmitting || jobSubmitted}
          sx={{ 
            width: '250px',
            fontSize: '1.2rem',
            py: 2,
            px: 4,
            borderRadius: 2
          }}
        >
          {isSubmitting ? 'Processing...' : 'Cut It!'}
        </Button>
        {(nameError || sqlValidationError) && (
          <Typography 
            variant="body2" 
            color="error" 
            sx={{ mt: 2 }}
          >
            {nameError || getSqlErrorMessage()}
          </Typography>
        )}
      </Box>

      {/* Status Messages */}
      {jobSubmitted && (
        <Box sx={{ 
          mt: 4,
          p: 3,
          borderRadius: 2,
          backgroundColor: '#f8f9fa'
        }}>
          {messages.map((msg, index) => (
            <Typography 
              key={index} 
              variant="body1" 
              sx={{ 
                color: msg.color,
                mb: 1,
                '&:last-child': { mb: 0 }
              }}
            >
              {msg.text}
            </Typography>
          ))}
          
          {/* Action buttons section - always shows job details, conditionally shows BigQuery view */}
          <Box sx={{ mt: 3, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Always show job details button if a job was submitted */}
            {jobId && (
              <Button 
                variant="contained"
                color="primary"
                component={Link}
                to={`/projects/${projectId}/list-cutter/${jobId}`}
                sx={{ fontSize: '1rem' }}
              >
                Go to job details page
              </Button>
            )}
            
            {/* Only show BigQuery view button for successfully completed jobs */}
            {jobState === 'DONE' && messages.some(msg => 
              typeof msg.text === 'string' && msg.text.includes('completed successfully')
            ) && (
              <ViewTableInBigQueryButton
                projectId={projectId}
                datasetId={targetDataset}
                tableId={targetTable}
                buttonText="View in BigQuery"
                variant="contained"
                color="secondary"
                sx={{ fontSize: '1rem' }}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ConfirmNewListCutterJob;
